import {
    useVariable as originalUseVariable,
    useVariableValue as originalUseVariableValue,
    DVCVariable,
    DVCVariableValue,
    DevCycleJSON
} from '@devcycle/react-client-sdk'

declare module '@devcycle/types' {
   interface CustomVariableDefinitions extends DVCVariableTypes {}
}

export type CustomData = {
    'payment_type'?: string
    'use_supplier_name'?: boolean
    'partner_programme'?: string
    'has_buyer_supplier_data'?: boolean
    'has_documents'?: boolean
    'has_contacts'?: boolean
    'has_awards'?: boolean
    'team_name'?: string
    'has_frameworks'?: boolean
    'subscription'?: string
    'team_id'?: string
    'company_name'?: string
    'company_id'?: string
}

export type UseVariableValue = <
    K extends string & keyof DVCVariableTypes
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariableTypes[K]

export const useVariableValue = originalUseVariableValue as UseVariableValue

export type UseVariable = <
    K extends string & keyof DVCVariableTypes,
    T extends DVCVariableValue & DVCVariableTypes[K],
>(
    key: K,
    defaultValue: DVCVariableTypes[K]
) => DVCVariable<T>

export const useVariable = originalUseVariable as UseVariable

export type DVCVariableTypes = {
    /**
     * key: require-better-auth-session
     * created by: Chris Parsons
     * created on: 2026-10-19
    */
    'require-better-auth-session': boolean
    /**
     * key: notice-insights-rail
     * description: Show the notice in one column with the Stotles insights rail beside it.
     * created by: Philippe Ziya
     * created on: 2026-10-19
    */
    'notice-insights-rail': boolean
    /**
     * key: zoho-crm
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-10-06
    */
    'zoho-crm': boolean
    /**
     * key: team-usage-stats
     * created by: Chris Parsons
     * created on: 2026-10-01
    */
    'team-usage-stats': boolean
    /**
     * key: crm-salesforce-migrated
     * created by: Manuel Silva
     * created on: 2026-09-28
    */
    'crm-salesforce-migrated': boolean
    /**
     * key: auth-sessions-list-in-ui
     * created by: Archie Lamb
     * created on: 2026-09-25
    */
    'auth-sessions-list-in-ui': boolean
    /**
     * key: self-serve-email-change
     * created by: Archie Lamb
     * created on: 2026-09-22
    */
    'self-serve-email-change': boolean
    /**
     * key: cognism-byot
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-09-22
    */
    'cognism-byot': boolean
    /**
     * key: crm-paragon-migrated
     * created by: Manuel Silva
     * created on: 2026-09-09
    */
    'crm-paragon-migrated': boolean
    /**
     * key: dynamics-crm
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-08-25
    */
    'dynamics-crm': boolean
    /**
     * key: leads-kanban
     * created by: Philippe Ziya
     * created on: 2026-08-20
    */
    'leads-kanban': boolean
    /**
     * key: better-auth-self-serve-sso
     * created by: Archie Lamb
     * created on: 2026-08-19
    */
    'better-auth-self-serve-sso': boolean
    /**
     * key: new-account-briefing
     * created by: Fionn McConville
     * created on: 2026-08-07
    */
    'new-account-briefing': boolean
    /**
     * key: mcp-integrations-page-tiles
     * created by: Daniel Jones
     * created on: 2026-08-07
    */
    'mcp-integrations-page-tiles': boolean
    /**
     * key: outreach-generator-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-08-03
    */
    'outreach-generator-feature-variant': string
    /**
     * key: lead-relevant-contacts-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-07-10
    */
    'lead-relevant-contacts-feature-variant': string
    /**
     * key: poc-lead-contacts
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-07-09
    */
    'poc-lead-contacts': boolean
    /**
     * key: better-auth
     * created by: Fionn McConville
     * created on: 2026-06-25
    */
    'better-auth': boolean
    /**
     * key: announcement-card
     * created by: Archie Lamb
     * created on: 2026-06-15
    */
    'announcement-card': DevCycleJSON
    /**
     * key: bulk-buying-signals
     * created by: Manuel Silva
     * created on: 2026-06-05
    */
    'bulk-buying-signals': boolean
    /**
     * key: cognism-contacts
     * created by: Manuel Silva
     * created on: 2026-05-26
    */
    'cognism-contacts': boolean
    /**
     * key: qualification-summary-feature-variant
     * created by: Fionn McConville
     * created on: 2026-05-14
    */
    'qualification-summary-feature-variant': string
    /**
     * key: document-single-qa-feature-variant
     * created by: Archie Lamb
     * created on: 2026-04-21
    */
    'document-single-qa-feature-variant': string
    /**
     * key: document-single-qa
     * created by: Archie Lamb
     * created on: 2026-04-21
    */
    'document-single-qa': boolean
    /**
     * key: tone-of-voice-document-summarization
     * created by: Fionn McConville
     * created on: 2026-04-20
    */
    'tone-of-voice-document-summarization': string
    /**
     * key: ai-report-config
     * created by: Chris Parsons
     * created on: 2026-04-08
    */
    'ai-report-config': boolean
    /**
     * key: documents-ai-features
     * created by: Manuel Silva
     * created on: 2026-04-07
    */
    'documents-ai-features': boolean
    /**
     * key: buyer-qa-variant
     * created by: Chris Parsons
     * created on: 2026-04-07
    */
    'buyer-qa-variant': string
    /**
     * key: buyer-qa
     * created by: Chris Parsons
     * created on: 2026-04-07
    */
    'buyer-qa': string
    /**
     * key: content-library-management
     * created by: Unknown User
     * created on: 2026-04-02
    */
    'content-library-management': boolean
    /**
     * key: tone-of-voice
     * created by: Unknown User
     * created on: 2026-04-02
    */
    'tone-of-voice': boolean
    /**
     * key: qualification-report-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-04-01
    */
    'qualification-report-feature-variant': string
    /**
     * key: bid-qualification-summary
     * created by: Fionn McConville
     * created on: 2026-03-31
    */
    'bid-qualification-summary': boolean
    /**
     * key: relevancy-search-emails
     * created by: Archie Lamb
     * created on: 2026-03-25
    */
    'relevancy-search-emails': boolean
    /**
     * key: bid-details-extraction-feature-variant
     * created by: Unknown User
     * created on: 2026-03-24
    */
    'bid-details-extraction-feature-variant': string
    /**
     * key: buyer-activity-generation-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-03-23
    */
    'buyer-activity-generation-feature-variant': string
    /**
     * key: related-notice-finder-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-03-23
    */
    'related-notice-finder-feature-variant': string
    /**
     * key: onboarding-company-topic-prompt-variant
     * created by: Chris Parsons
     * created on: 2026-03-23
    */
    'onboarding-company-topic-prompt-variant': string
    /**
     * key: relevancy-search-query-variation
     * created by: Manuel Silva
     * created on: 2026-03-17
    */
    'relevancy-search-query-variation': string
    /**
     * key: relevancy-search-new-users
     * created by: Chris Parsons
     * created on: 2026-03-12
    */
    'relevancy-search-new-users': boolean
    /**
     * key: win-themes-resolver-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-03-05
    */
    'win-themes-resolver-feature-variant': string
    /**
     * key: bid-report-from-tender
     * created by: Unknown User
     * created on: 2026-03-03
     * @stale This variable is part of "Bid report from tender" feature with stale reason: released. Recommended value to set it to: true

    */
    'bid-report-from-tender': boolean
    /**
     * key: bid-report-chat-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2026-02-26
     * @stale This variable is part of "Bid report chat" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

    */
    'bid-report-chat-feature-variant': string
    /**
     * key: relevancy-search
     * created by: Chris Parsons
     * created on: 2026-02-25
    */
    'relevancy-search': boolean
    /**
     * key: bid-docs-question-extraction-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-09-11
    */
    'bid-docs-question-extraction-feature-variant': string
    /**
     * key: csp-enforce
     * created by: Unknown User
     * created on: 2025-09-10
    */
    'csp-enforce': boolean
    /**
     * key: bid-response-chat-complex-analysis-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-complex-analysis-feature-variant': string
    /**
     * key: bid-response-chat-writing-drafting-formatting-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-writing-drafting-formatting-feature-variant': string
    /**
     * key: bid-response-chat-historical-company-content-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-historical-company-content-feature-variant': string
    /**
     * key: bid-response-understand-buyer-and-market-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-understand-buyer-and-market-feature-variant': string
    /**
     * key: bid-response-chat-other-catch-all-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-other-catch-all-feature-variant': string
    /**
     * key: bid-response-chat-clarification-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-clarification-feature-variant': string
    /**
     * key: bid-response-chat-understand-bid-and-specs-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-understand-bid-and-specs-feature-variant': string
    /**
     * key: bid-response-chat-routing-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-08-22
    */
    'bid-response-chat-routing-feature-variant': string
    /**
     * key: new-items-workflow
     * created by: Archie Lamb
     * created on: 2025-08-12
    */
    'new-items-workflow': boolean
    /**
     * key: frameworks-fuzzy-search
     * created by: Unknown User
     * created on: 2025-08-11
     * @stale This variable is part of "Frameworks fuzzy search" feature with stale reason: released. Recommended value to set it to: true

    */
    'frameworks-fuzzy-search': boolean
    /**
     * key: customer-document-chat-feature-variant
     * created by: Sashika Bamunurathna Mudiyanselage
     * created on: 2025-08-06
     * @stale This variable is part of "Customer document chat" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

    */
    'customer-document-chat-feature-variant': string
    /**
     * key: bid-writing-question-feature-variant
     * created by: Dominic Bruton
     * created on: 2025-07-28
     * @stale This variable is part of "Bid writing question" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

    */
    'bid-writing-question-feature-variant': string
    /**
     * key: tender-documents-semantic-search-mode
     * created by: Gabriella Abraham
     * created on: 2025-07-03
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: SINGLE_STRING

    */
    'tender-documents-semantic-search-mode': string
    /**
     * key: bid-qualification-variant
     * created by: Unknown User
     * created on: 2025-06-25
    */
    'bid-qualification-variant': string
    /**
     * key: max_parallel_document_uploads
     * created by: Fionn McConville
     * created on: 2025-04-25
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 10

    */
    'max_parallel_document_uploads': number
    /**
     * key: prompt-key-override
     * created by: Unknown User
     * created on: 2025-04-24
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: bid-module-base-prompt

    */
    'prompt-key-override': string
    /**
     * key: llm-model
     * created by: Unknown User
     * created on: 2025-04-24
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: gpt-4.1

    */
    'llm-model': string
    /**
     * key: dedupe-result-chunks
     * created by: Unknown User
     * created on: 2025-04-23
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: true

    */
    'dedupe-result-chunks': boolean
    /**
     * key: keyword-search-function
     * created by: Unknown User
     * created on: 2025-04-01
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: false

    */
    'keyword-search-function': boolean
    /**
     * key: semantic-search-result-count
     * created by: Unknown User
     * created on: 2025-03-31
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 5

    */
    'semantic-search-result-count': number
    /**
     * key: qualify-bid-concurrency
     * description: The number which controls how many qualifyBid questions we run in parallel
     * created by: Fionn McConville
     * created on: 2025-03-14
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 20

    */
    'qualify-bid-concurrency': number
    /**
     * key: bid-question-variables
     * description: A list of variables that can be adjusted to improve LLM accuracy
     * created by: Unknown User
     * created on: 2025-03-10
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 
    {
        "fullDocumentFunctionDescription": "Retrieve the full text of the document. This is useful if more context is needed to answer the question. It requires the document key which can be retrieved from the semantic search results. You can use this function multiple times, but only call this function once per document.",
        "noticeDetailsFunctionDescription": "Retrieve the data contained on a notice relating to a tender process. Information includes name, description, value, relevant dates etc. This function requires no parameters. Use this function exactly once. \n\nWhen returning information from the notice, ensure the response follows this JSON format:\n\n{\n  \"answer\": \"<Concise and accurate information>\",\n  \"sources\": [\n    {\n      \"document\": \"STOTLES_NOTICE\",\n      \"documentId\": \"STOTLES_NOTICE\",\n      \"chunkNumber\": -1\n    }\n  ]\n}\n\n- Ensure both `document` and `documentId` are always \"STOTLES_NOTICE\".\n- Set `chunkNumber` to `-1` unless a specific chunk is relevant.\n- Do not include extraneous information or incorrect formatting.\n\nExample response:\n\n{\n  \"answer\": \"The contract is expected to start on 1st May 2025.\",\n  \"sources\": [\n    {\n      \"document\": \"STOTLES_NOTICE\",\n      \"documentId\": \"STOTLES_NOTICE\",\n      \"chunkNumber\": -1\n    }\n  ]\n}",
        "semanticSearchFunctionDescription": "Semantically search keywords inside the tender documents related to the question. It will return the results of the search and small chunks of the document. You can call it multiple times with other useful keywords to get more context.",
        "keywordSearchFunctionDescription": "Keyword search for the tender documents related to the question. It will return the results of the search and a small chunk of the document. You can call it again with another search text to get more context. Do not call more than 10 times and should call it at least once. Only one word is allowed, it should contain no spaces"
    }


    */
    'bid-question-variables': DevCycleJSON
    /**
     * key: context-chunk-size
     * created by: Unknown User
     * created on: 2025-02-27
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 3

    */
    'context-chunk-size': number
    /**
     * key: notice-details-function
     * created by: Unknown User
     * created on: 2025-02-14
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: true

    */
    'notice-details-function': boolean
    /**
     * key: organisations-signal-filtering
     * created by: Dominic Bruton
     * created on: 2025-02-13
     * @stale This variable is part of "Organisations Signal filtering" feature with stale reason: unmodifiedLong. Recommended value to set it to: undefined

    */
    'organisations-signal-filtering': boolean
    /**
     * key: bid-document-processor
     * created by: Unknown User
     * created on: 2025-02-11
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: APRYSE_IDE

    */
    'bid-document-processor': string
    /**
     * key: allow-bid-requalify
     * created by: Fionn McConville
     * created on: 2025-01-13
     * @stale This variable is part of "allow-bid-requalify" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

    */
    'allow-bid-requalify': boolean
    /**
     * key: enable-full-document-search
     * description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
     * created by: Unknown User
     * created on: 2025-01-08
     * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: false

    */
    'enable-full-document-search': boolean
}

/**
 * key: require-better-auth-session
 * created by: Chris Parsons
 * created on: 2026-10-19
*/

export const REQUIRE_BETTER_AUTH_SESSION = 'require-better-auth-session' as const

/**
 * key: notice-insights-rail
 * description: Show the notice in one column with the Stotles insights rail beside it.
 * created by: Philippe Ziya
 * created on: 2026-10-19
*/

export const NOTICE_INSIGHTS_RAIL = 'notice-insights-rail' as const

/**
 * key: zoho-crm
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-10-06
*/

export const ZOHO_CRM = 'zoho-crm' as const

/**
 * key: team-usage-stats
 * created by: Chris Parsons
 * created on: 2026-10-01
*/

export const TEAM_USAGE_STATS = 'team-usage-stats' as const

/**
 * key: crm-salesforce-migrated
 * created by: Manuel Silva
 * created on: 2026-09-28
*/

export const CRM_SALESFORCE_MIGRATED = 'crm-salesforce-migrated' as const

/**
 * key: auth-sessions-list-in-ui
 * created by: Archie Lamb
 * created on: 2026-09-25
*/

export const AUTH_SESSIONS_LIST_IN_UI = 'auth-sessions-list-in-ui' as const

/**
 * key: self-serve-email-change
 * created by: Archie Lamb
 * created on: 2026-09-22
*/

export const SELF_SERVE_EMAIL_CHANGE = 'self-serve-email-change' as const

/**
 * key: cognism-byot
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-09-22
*/

export const COGNISM_BYOT = 'cognism-byot' as const

/**
 * key: crm-paragon-migrated
 * created by: Manuel Silva
 * created on: 2026-09-09
*/

export const CRM_PARAGON_MIGRATED = 'crm-paragon-migrated' as const

/**
 * key: dynamics-crm
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-08-25
*/

export const DYNAMICS_CRM = 'dynamics-crm' as const

/**
 * key: leads-kanban
 * created by: Philippe Ziya
 * created on: 2026-08-20
*/

export const LEADS_KANBAN = 'leads-kanban' as const

/**
 * key: better-auth-self-serve-sso
 * created by: Archie Lamb
 * created on: 2026-08-19
*/

export const BETTER_AUTH_SELF_SERVE_SSO = 'better-auth-self-serve-sso' as const

/**
 * key: new-account-briefing
 * created by: Fionn McConville
 * created on: 2026-08-07
*/

export const NEW_ACCOUNT_BRIEFING = 'new-account-briefing' as const

/**
 * key: mcp-integrations-page-tiles
 * created by: Daniel Jones
 * created on: 2026-08-07
*/

export const MCP_INTEGRATIONS_PAGE_TILES = 'mcp-integrations-page-tiles' as const

/**
 * key: outreach-generator-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-08-03
*/

export const OUTREACH_GENERATOR_FEATURE_VARIANT = 'outreach-generator-feature-variant' as const

/**
 * key: lead-relevant-contacts-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-07-10
*/

export const LEAD_RELEVANT_CONTACTS_FEATURE_VARIANT = 'lead-relevant-contacts-feature-variant' as const

/**
 * key: poc-lead-contacts
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-07-09
*/

export const POC_LEAD_CONTACTS = 'poc-lead-contacts' as const

/**
 * key: better-auth
 * created by: Fionn McConville
 * created on: 2026-06-25
*/

export const BETTER_AUTH = 'better-auth' as const

/**
 * key: announcement-card
 * created by: Archie Lamb
 * created on: 2026-06-15
*/

export const ANNOUNCEMENT_CARD = 'announcement-card' as const

/**
 * key: bulk-buying-signals
 * created by: Manuel Silva
 * created on: 2026-06-05
*/

export const BULK_BUYING_SIGNALS = 'bulk-buying-signals' as const

/**
 * key: cognism-contacts
 * created by: Manuel Silva
 * created on: 2026-05-26
*/

export const COGNISM_CONTACTS = 'cognism-contacts' as const

/**
 * key: qualification-summary-feature-variant
 * created by: Fionn McConville
 * created on: 2026-05-14
*/

export const QUALIFICATION_SUMMARY_FEATURE_VARIANT = 'qualification-summary-feature-variant' as const

/**
 * key: document-single-qa-feature-variant
 * created by: Archie Lamb
 * created on: 2026-04-21
*/

export const DOCUMENT_SINGLE_QA_FEATURE_VARIANT = 'document-single-qa-feature-variant' as const

/**
 * key: document-single-qa
 * created by: Archie Lamb
 * created on: 2026-04-21
*/

export const DOCUMENT_SINGLE_QA = 'document-single-qa' as const

/**
 * key: tone-of-voice-document-summarization
 * created by: Fionn McConville
 * created on: 2026-04-20
*/

export const TONE_OF_VOICE_DOCUMENT_SUMMARIZATION = 'tone-of-voice-document-summarization' as const

/**
 * key: ai-report-config
 * created by: Chris Parsons
 * created on: 2026-04-08
*/

export const AI_REPORT_CONFIG = 'ai-report-config' as const

/**
 * key: documents-ai-features
 * created by: Manuel Silva
 * created on: 2026-04-07
*/

export const DOCUMENTS_AI_FEATURES = 'documents-ai-features' as const

/**
 * key: buyer-qa-variant
 * created by: Chris Parsons
 * created on: 2026-04-07
*/

export const BUYER_QA_VARIANT = 'buyer-qa-variant' as const

/**
 * key: buyer-qa
 * created by: Chris Parsons
 * created on: 2026-04-07
*/

export const BUYER_QA = 'buyer-qa' as const

/**
 * key: content-library-management
 * created by: Unknown User
 * created on: 2026-04-02
*/

export const CONTENT_LIBRARY_MANAGEMENT = 'content-library-management' as const

/**
 * key: tone-of-voice
 * created by: Unknown User
 * created on: 2026-04-02
*/

export const TONE_OF_VOICE = 'tone-of-voice' as const

/**
 * key: qualification-report-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-04-01
*/

export const QUALIFICATION_REPORT_FEATURE_VARIANT = 'qualification-report-feature-variant' as const

/**
 * key: bid-qualification-summary
 * created by: Fionn McConville
 * created on: 2026-03-31
*/

export const BID_QUALIFICATION_SUMMARY = 'bid-qualification-summary' as const

/**
 * key: relevancy-search-emails
 * created by: Archie Lamb
 * created on: 2026-03-25
*/

export const RELEVANCY_SEARCH_EMAILS = 'relevancy-search-emails' as const

/**
 * key: bid-details-extraction-feature-variant
 * created by: Unknown User
 * created on: 2026-03-24
*/

export const BID_DETAILS_EXTRACTION_FEATURE_VARIANT = 'bid-details-extraction-feature-variant' as const

/**
 * key: buyer-activity-generation-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-03-23
*/

export const BUYER_ACTIVITY_GENERATION_FEATURE_VARIANT = 'buyer-activity-generation-feature-variant' as const

/**
 * key: related-notice-finder-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-03-23
*/

export const RELATED_NOTICE_FINDER_FEATURE_VARIANT = 'related-notice-finder-feature-variant' as const

/**
 * key: onboarding-company-topic-prompt-variant
 * created by: Chris Parsons
 * created on: 2026-03-23
*/

export const ONBOARDING_COMPANY_TOPIC_PROMPT_VARIANT = 'onboarding-company-topic-prompt-variant' as const

/**
 * key: relevancy-search-query-variation
 * created by: Manuel Silva
 * created on: 2026-03-17
*/

export const RELEVANCY_SEARCH_QUERY_VARIATION = 'relevancy-search-query-variation' as const

/**
 * key: relevancy-search-new-users
 * created by: Chris Parsons
 * created on: 2026-03-12
*/

export const RELEVANCY_SEARCH_NEW_USERS = 'relevancy-search-new-users' as const

/**
 * key: win-themes-resolver-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-03-05
*/

export const WIN_THEMES_RESOLVER_FEATURE_VARIANT = 'win-themes-resolver-feature-variant' as const

/**
 * key: bid-report-from-tender
 * created by: Unknown User
 * created on: 2026-03-03
 * @stale This variable is part of "Bid report from tender" feature with stale reason: released. Recommended value to set it to: true

*/

export const BID_REPORT_FROM_TENDER = 'bid-report-from-tender' as const

/**
 * key: bid-report-chat-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2026-02-26
 * @stale This variable is part of "Bid report chat" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

*/

export const BID_REPORT_CHAT_FEATURE_VARIANT = 'bid-report-chat-feature-variant' as const

/**
 * key: relevancy-search
 * created by: Chris Parsons
 * created on: 2026-02-25
*/

export const RELEVANCY_SEARCH = 'relevancy-search' as const

/**
 * key: bid-docs-question-extraction-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-09-11
*/

export const BID_DOCS_QUESTION_EXTRACTION_FEATURE_VARIANT = 'bid-docs-question-extraction-feature-variant' as const

/**
 * key: csp-enforce
 * created by: Unknown User
 * created on: 2025-09-10
*/

export const CSP_ENFORCE = 'csp-enforce' as const

/**
 * key: bid-response-chat-complex-analysis-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_COMPLEX_ANALYSIS_FEATURE_VARIANT = 'bid-response-chat-complex-analysis-feature-variant' as const

/**
 * key: bid-response-chat-writing-drafting-formatting-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_WRITING_DRAFTING_FORMATTING_FEATURE_VARIANT = 'bid-response-chat-writing-drafting-formatting-feature-variant' as const

/**
 * key: bid-response-chat-historical-company-content-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_HISTORICAL_COMPANY_CONTENT_FEATURE_VARIANT = 'bid-response-chat-historical-company-content-feature-variant' as const

/**
 * key: bid-response-understand-buyer-and-market-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_UNDERSTAND_BUYER_AND_MARKET_FEATURE_VARIANT = 'bid-response-understand-buyer-and-market-feature-variant' as const

/**
 * key: bid-response-chat-other-catch-all-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_OTHER_CATCH_ALL_FEATURE_VARIANT = 'bid-response-chat-other-catch-all-feature-variant' as const

/**
 * key: bid-response-chat-clarification-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_CLARIFICATION_FEATURE_VARIANT = 'bid-response-chat-clarification-feature-variant' as const

/**
 * key: bid-response-chat-understand-bid-and-specs-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_UNDERSTAND_BID_AND_SPECS_FEATURE_VARIANT = 'bid-response-chat-understand-bid-and-specs-feature-variant' as const

/**
 * key: bid-response-chat-routing-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-08-22
*/

export const BID_RESPONSE_CHAT_ROUTING_FEATURE_VARIANT = 'bid-response-chat-routing-feature-variant' as const

/**
 * key: new-items-workflow
 * created by: Archie Lamb
 * created on: 2025-08-12
*/

export const NEW_ITEMS_WORKFLOW = 'new-items-workflow' as const

/**
 * key: frameworks-fuzzy-search
 * created by: Unknown User
 * created on: 2025-08-11
 * @stale This variable is part of "Frameworks fuzzy search" feature with stale reason: released. Recommended value to set it to: true

*/

export const FRAMEWORKS_FUZZY_SEARCH = 'frameworks-fuzzy-search' as const

/**
 * key: customer-document-chat-feature-variant
 * created by: Sashika Bamunurathna Mudiyanselage
 * created on: 2025-08-06
 * @stale This variable is part of "Customer document chat" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

*/

export const CUSTOMER_DOCUMENT_CHAT_FEATURE_VARIANT = 'customer-document-chat-feature-variant' as const

/**
 * key: bid-writing-question-feature-variant
 * created by: Dominic Bruton
 * created on: 2025-07-28
 * @stale This variable is part of "Bid writing question" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

*/

export const BID_WRITING_QUESTION_FEATURE_VARIANT = 'bid-writing-question-feature-variant' as const

/**
 * key: tender-documents-semantic-search-mode
 * created by: Gabriella Abraham
 * created on: 2025-07-03
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: SINGLE_STRING

*/

export const TENDER_DOCUMENTS_SEMANTIC_SEARCH_MODE = 'tender-documents-semantic-search-mode' as const

/**
 * key: bid-qualification-variant
 * created by: Unknown User
 * created on: 2025-06-25
*/

export const BID_QUALIFICATION_VARIANT = 'bid-qualification-variant' as const

/**
 * key: max_parallel_document_uploads
 * created by: Fionn McConville
 * created on: 2025-04-25
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 10

*/

export const MAX_PARALLEL_DOCUMENT_UPLOADS = 'max_parallel_document_uploads' as const

/**
 * key: prompt-key-override
 * created by: Unknown User
 * created on: 2025-04-24
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: bid-module-base-prompt

*/

export const PROMPT_KEY_OVERRIDE = 'prompt-key-override' as const

/**
 * key: llm-model
 * created by: Unknown User
 * created on: 2025-04-24
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: gpt-4.1

*/

export const LLM_MODEL = 'llm-model' as const

/**
 * key: dedupe-result-chunks
 * created by: Unknown User
 * created on: 2025-04-23
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: true

*/

export const DEDUPE_RESULT_CHUNKS = 'dedupe-result-chunks' as const

/**
 * key: keyword-search-function
 * created by: Unknown User
 * created on: 2025-04-01
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: false

*/

export const KEYWORD_SEARCH_FUNCTION = 'keyword-search-function' as const

/**
 * key: semantic-search-result-count
 * created by: Unknown User
 * created on: 2025-03-31
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 5

*/

export const SEMANTIC_SEARCH_RESULT_COUNT = 'semantic-search-result-count' as const

/**
 * key: qualify-bid-concurrency
 * description: The number which controls how many qualifyBid questions we run in parallel
 * created by: Fionn McConville
 * created on: 2025-03-14
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 20

*/

export const QUALIFY_BID_CONCURRENCY = 'qualify-bid-concurrency' as const

/**
 * key: bid-question-variables
 * description: A list of variables that can be adjusted to improve LLM accuracy
 * created by: Unknown User
 * created on: 2025-03-10
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 
{
    "fullDocumentFunctionDescription": "Retrieve the full text of the document. This is useful if more context is needed to answer the question. It requires the document key which can be retrieved from the semantic search results. You can use this function multiple times, but only call this function once per document.",
    "noticeDetailsFunctionDescription": "Retrieve the data contained on a notice relating to a tender process. Information includes name, description, value, relevant dates etc. This function requires no parameters. Use this function exactly once. \n\nWhen returning information from the notice, ensure the response follows this JSON format:\n\n{\n  \"answer\": \"<Concise and accurate information>\",\n  \"sources\": [\n    {\n      \"document\": \"STOTLES_NOTICE\",\n      \"documentId\": \"STOTLES_NOTICE\",\n      \"chunkNumber\": -1\n    }\n  ]\n}\n\n- Ensure both `document` and `documentId` are always \"STOTLES_NOTICE\".\n- Set `chunkNumber` to `-1` unless a specific chunk is relevant.\n- Do not include extraneous information or incorrect formatting.\n\nExample response:\n\n{\n  \"answer\": \"The contract is expected to start on 1st May 2025.\",\n  \"sources\": [\n    {\n      \"document\": \"STOTLES_NOTICE\",\n      \"documentId\": \"STOTLES_NOTICE\",\n      \"chunkNumber\": -1\n    }\n  ]\n}",
    "semanticSearchFunctionDescription": "Semantically search keywords inside the tender documents related to the question. It will return the results of the search and small chunks of the document. You can call it multiple times with other useful keywords to get more context.",
    "keywordSearchFunctionDescription": "Keyword search for the tender documents related to the question. It will return the results of the search and a small chunk of the document. You can call it again with another search text to get more context. Do not call more than 10 times and should call it at least once. Only one word is allowed, it should contain no spaces"
}


*/

export const BID_QUESTION_VARIABLES = 'bid-question-variables' as const

/**
 * key: context-chunk-size
 * created by: Unknown User
 * created on: 2025-02-27
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: 3

*/

export const CONTEXT_CHUNK_SIZE = 'context-chunk-size' as const

/**
 * key: notice-details-function
 * created by: Unknown User
 * created on: 2025-02-14
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: true

*/

export const NOTICE_DETAILS_FUNCTION = 'notice-details-function' as const

/**
 * key: organisations-signal-filtering
 * created by: Dominic Bruton
 * created on: 2025-02-13
 * @stale This variable is part of "Organisations Signal filtering" feature with stale reason: unmodifiedLong. Recommended value to set it to: undefined

*/

export const ORGANISATIONS_SIGNAL_FILTERING = 'organisations-signal-filtering' as const

/**
 * key: bid-document-processor
 * created by: Unknown User
 * created on: 2025-02-11
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: APRYSE_IDE

*/

export const BID_DOCUMENT_PROCESSOR = 'bid-document-processor' as const

/**
 * key: allow-bid-requalify
 * created by: Fionn McConville
 * created on: 2025-01-13
 * @stale This variable is part of "allow-bid-requalify" feature with stale reason: unmodifiedShort. Recommended value to set it to: undefined

*/

export const ALLOW_BID_REQUALIFY = 'allow-bid-requalify' as const

/**
 * key: enable-full-document-search
 * description: Keep the additional OpenAI function that allows the AI to search the full documents. Ideally going forward we want to disable this as it will hit token limits for OpenAI
 * created by: Unknown User
 * created on: 2025-01-08
 * @stale This variable is part of "Bid Module POC" feature with stale reason: released. Recommended value to set it to: false

*/

export const ENABLE_FULL_DOCUMENT_SEARCH = 'enable-full-document-search' as const
